import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { settleIntakeNow } from './api';
import { clearDraft } from './draftStorage';
import { IntakeConversation } from './IntakeConversation';
import { ThinkingChecklist } from './PhaseIndicator';
import { intakeQueryKeys, useIntakeState } from './useIntakeState';
import type { IntakeState } from './types';

/**
 * The intake conversation, on the request screen.
 *
 * This exists for the client who did not wait. Posting opens the
 * conversation in the ask box, but a person who is impatient — or who
 * clicked "Open request #339 →", or closed the tab and came back through
 * their email — is looking at the request, not at Home. If the follow-up
 * questions only lived in the ask box they would arrive somewhere nobody is
 * watching, and the request would sit there looking finished while the one
 * thing that would make it workable went unasked.
 *
 * So the same conversation renders here, reading the same server state
 * through the same hook. Answering it here and answering it in the ask box
 * are the same act, and whichever screen is open when the round lands is the
 * one that shows it.
 *
 * Renders nothing once intake is terminal, which is the overwhelmingly
 * common case — every request that has been around for more than a minute.
 * The proposal is deliberately not repeated: the request's own fields and
 * title are right above this, and showing the AI's read of them a second
 * time would be duplicate furniture rather than information.
 */
export function RequestIntakeSection({
  workspaceId,
  requestId,
}: {
  readonly workspaceId: string;
  readonly requestId: string;
}) {
  const queryClient = useQueryClient();
  const query = useIntakeState(workspaceId, requestId);
  const state = query.data;

  const settle = useMutation({
    mutationFn: () => settleIntakeNow(workspaceId, requestId),
    onSuccess: (next: IntakeState) =>
      queryClient.setQueryData<IntakeState>(intakeQueryKeys.intake(workspaceId, requestId), next),
  });

  // The request's own row changes at settle — title, type, project, estimate are
  // all written then — so this screen has to re-read it or it keeps showing
  // the placeholder first line the client typed.
  //
  // Only on a transition seen here. A request that was already settled when it
  // was opened — most of them, and all imported history — has nothing to
  // re-read, and must not wipe the unrelated draft sitting in the ask box.
  const settledHandled = useRef(false);
  const sawInFlight = useRef(false);
  useEffect(() => {
    if (state?.status === 'thinking' || state?.status === 'awaiting_answers') sawInFlight.current = true;
    if (state?.status !== 'settled' || !sawInFlight.current || settledHandled.current) return;
    settledHandled.current = true;
    clearDraft(workspaceId);
    void queryClient.invalidateQueries({ queryKey: queryKeys.request(workspaceId, requestId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.requests(workspaceId) });
  }, [state?.status, workspaceId, requestId, queryClient]);

  // Nothing to say on a request whose conversation is over, which is almost
  // all of them. An error is silent for the same reason: the request itself
  // renders fine without this, and a failure to reach intake is not something
  // to put in front of a client reading their own request.
  if (state === undefined || query.error !== null) return null;
  if (state.status === 'settled' || state.status === 'failed') return null;

  const isThinking = state.status === 'thinking';

  return (
    <section className="rounded-[10px] border border-accent-border bg-accent-tint-soft px-4 py-3">
      <div className="mb-2">
        <span className="fs-11 font-bold tracking-[0.06em] text-accent uppercase">
          {isThinking ? 'Reading your request' : 'A couple of quick questions'}
        </span>
      </div>

      {isThinking ? (
        <>
          <ThinkingChecklist
            phases={state.phases}
            step={state.phaseStep}
            fallback={state.phase ?? 'Thinking it through…'}
          />
          <p className="mt-2.5 fs-11 text-faint">
            Your request is saved. If anything needs clarifying, it will appear here in a moment — you don't
            have to wait for it.
          </p>
        </>
      ) : (
        <>
          <p className="mb-3 fs-12 text-muted">Answering these means we can start without guessing.</p>
          <IntakeConversation
            workspaceId={workspaceId}
            requestId={requestId}
            state={state}
            showSummary={false}
            escape={
              <button
                type="button"
                onClick={() => settle.mutate()}
                disabled={settle.isPending}
                className="rounded-lg px-2.5 py-2 fs-12 font-semibold text-muted hover:text-ink disabled:opacity-50"
              >
                Skip and post
              </button>
            }
          />
        </>
      )}
    </section>
  );
}
